import { EntityAndBranch } from '@agilelab/plugin-wb-builder-common';
import {
  customAlertApiRef,
  WbButton,
  WbLink,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { builderDpSnapshotCreatePermission } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { getEntitySourceLocation } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { ButtonProps } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import parseGitUrl from 'git-url-parse';
import { FC, useMemo } from 'react';
import { panelCatalogApiRef } from '../../api';
import { releaseDetailRouteRef } from '../../routes';
import { generateSnapshotButtonTooltip, getReleaseInfo } from '../../utils';
import { useReleasePageContext } from './context/useReleasePageContext';

type Props = ButtonProps;

export const NewDraftReleaseButton: FC<Props> = props => {
  const {
    isLoading,
    isReleasing,
    setIsReleasing,
    addRelease,
    entity,
    releases,
    relatedEntities,
    processorErrors,
  } = useReleasePageContext();
  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);

  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);

  const { allowed: canCreateSnapshot } = usePermission({
    permission: builderDpSnapshotCreatePermission,
    resourceRef: stringifyEntityRef({
      kind: entity.kind ?? 'system',
      namespace: 'default',
      name: entity.metadata.name,
    }),
  });

  const createSnapshot = async () => {
    try {
      const entities: EntityAndBranch[] = [entity, ...relatedEntities!].map(
        en => {
          const entitySourceLocation = getEntitySourceLocation(
            en,
            scmIntegrationsApi,
          );
          const branchFromLocationUrl = entitySourceLocation?.locationTargetUrl
            ? parseGitUrl(entitySourceLocation.locationTargetUrl).ref
            : 'master';

          return {
            branch: branchFromLocationUrl,
            entityRef: stringifyEntityRef(en),
          };
        },
      );

      setIsReleasing(true);
      const newRelease = await panelCatalog.createSnapshot(
        entity.metadata.name,
        entity.kind,
        entities,
      );

      const { kind, namespace, name } = getReleaseInfo(newRelease);

      addRelease({ ...newRelease, deployments: [] });
      alertApi.post({
        actions: (
          <WbLink
            route={releaseDetailRoute({ kind, namespace, name })}
            text={`Open V${newRelease?.metadata?.version}`}
          />
        ),
        message: 'New Draft-Release created successfully.',
        severity: 'success',
      });
    } catch (error) {
      alertApi.post({
        error,
        severity: 'error',
      });
    } finally {
      setIsReleasing(false);
    }
  };

  const hasAlreadySnapshot = useMemo(
    () => Boolean(releases.find(release => release.metadata.isSnapshot)),
    [releases],
  );

  return (
    <WbTooltip
      title={generateSnapshotButtonTooltip(
        canCreateSnapshot,
        hasAlreadySnapshot,
        processorErrors,
      )}
    >
      <span>
        <WbButton
          icon={<AddCircleIcon />}
          label="New Draft Release"
          loading={isReleasing}
          disabled={
            isLoading ||
            hasAlreadySnapshot ||
            !canCreateSnapshot ||
            !!processorErrors?.length
          }
          onClick={createSnapshot}
          {...props}
        />
      </span>
    </WbTooltip>
  );
};
